/**
 * Body of the parcel details card: what the parcel weighs and how big it is - the two things a
 * carrier quotes on, and the two the merchant is most often here to correct.
 *
 * The units belong to the parcel rather than to a field, and picking another one relabels the
 * numbers instead of converting them: the merchant is saying which scale they were read off.
 *
 * The box is all or none. A method that sends no box of its own comes without one, and may go back
 * without one; once any side is filled, every side and the unit are asked for.
 */
import { observer } from "mobx-react";
import { FormFieldLine } from "../../../../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../../../../components/inputs/input/input";
import { SelectDropdown } from "../../../../../../../../components/inputs/select-dropdown/select-dropdown";
import ListLayout, { LIST_LAYOUT_SPACING_VARIANT } from "../../../../../../../../components/layouts/list-layout/list-layout";
import { CP_DIMENSION_UNIT, WEIGHT_UNIT } from "../../../../../../../return-methods/return-method-types";
import { IValidateShippingDetailsProps } from "../validate-shipping-details-props";
import { greaterThanZero } from "./parcel-details-validation";
import SectionTitle from "./section-title/section-title";
import ShipmentWeight from "./shipment-weight/shipment-weight";

const ParcelDetails: React.FC<IValidateShippingDetailsProps> = observer(({ store, validationGroup }) => {
    const parcel = store.parcel;
    const hasDimensions = parcel.hasDimensions;

    return (
        <ListLayout spacingVariant={LIST_LAYOUT_SPACING_VARIANT.SMAll}>
            <SectionTitle title="Weight" />

            <FormFieldLine label="Weight unit" required>
                <SelectDropdown
                    value={parcel.selectedWeightUnit}
                    options={parcel.weightUnitOptions}
                    onChange={(option) => option && parcel.setWeightUnit(option.value as WEIGHT_UNIT)}
                    required
                    validationGroup={validationGroup}
                />
            </FormFieldLine>

            <ShipmentWeight parcel={parcel} validationGroup={validationGroup} />

            <SectionTitle title="Dimensions" />

            <FormFieldLine label="Dimension unit" required={hasDimensions}>
                <SelectDropdown
                    value={parcel.selectedDistanceUnit}
                    options={parcel.distanceUnitOptions}
                    onChange={(option) => option && parcel.setDistanceUnit(option.value as CP_DIMENSION_UNIT)}
                    required={hasDimensions}
                    validationGroup={validationGroup}
                />
            </FormFieldLine>

            <FormFieldLine label="Length" required={hasDimensions}>
                <Input
                    type="number"
                    min={0}
                    temp
                    value={parcel.length}
                    onChange={parcel.setLength}
                    required={hasDimensions}
                    validationGroup={validationGroup}
                    validationCallback={() => greaterThanZero(parcel.length)}
                />
            </FormFieldLine>

            <FormFieldLine label="Width" required={hasDimensions}>
                <Input
                    type="number"
                    min={0}
                    temp
                    value={parcel.width}
                    onChange={parcel.setWidth}
                    required={hasDimensions}
                    validationGroup={validationGroup}
                    validationCallback={() => greaterThanZero(parcel.width)}
                />
            </FormFieldLine>

            <FormFieldLine label="Height" required={hasDimensions}>
                <Input
                    type="number"
                    min={0}
                    temp
                    value={parcel.height}
                    onChange={parcel.setHeight}
                    required={hasDimensions}
                    validationGroup={validationGroup}
                    validationCallback={() => greaterThanZero(parcel.height)}
                />
            </FormFieldLine>
        </ListLayout>
    );
});

export default ParcelDetails;
